package com.yogaaicoach.pages.payment.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.yogaaicoach.pages.auth.TokenDataManager
import com.yogaaicoach.pages.payment.data.PaymentApi
import com.yogaaicoach.user.AppViewModel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SubscriptionDetailsUiState(
    val plan: String = "",
    val nextBillingDate: String = "",
    val isCancelled: Boolean = false,
    val isLoading: Boolean = true,
    val error: String? = null
)

class ManageSubscriptionViewModel(private val paymentApi: PaymentApi) : ViewModel() {

    private val _uiState = MutableStateFlow(SubscriptionDetailsUiState())
    val uiState: StateFlow<SubscriptionDetailsUiState> = _uiState.asStateFlow()

    var isPageLoaded by mutableStateOf(false)
        private set

    // StateFlow variants for jsMain React collectors (mutableStateOf is not observable there).
    private val _isPageLoadedFlow = MutableStateFlow(false)
    val isPageLoadedFlow: StateFlow<Boolean> = _isPageLoadedFlow.asStateFlow()

    var isSuccessVerified by mutableStateOf(false)
        private set

    private val _isSuccessVerifiedFlow = MutableStateFlow(false)
    val isSuccessVerifiedFlow: StateFlow<Boolean> = _isSuccessVerifiedFlow.asStateFlow()

    fun loadPage(appViewModel: AppViewModel, onNoSubscriptionFoundRedirect: () -> Unit) {
        if (!isPageLoaded) {
            isPageLoaded = true
            _isPageLoadedFlow.value = true
            viewModelScope.launch {
                // TODO this redirect has to show message too, that couldn't verify subscription, Please reload page and if still no subscription please contact us"
                verifyItHasSubscription(appViewModel, onNoSubscriptionFoundRedirect)
                getSubscriptionDetails()
            }
        }
    }

    private suspend fun verifyItHasSubscription(appViewModel: AppViewModel, onNoSubscriptionFoundRedirect: () -> Unit) {
        // If new subscription then it will take some time before it's true
        var counter = 0
        while (TokenDataManager.tokenData?.isSubscribed != true) {
            delay(1000)
            appViewModel.askNewToken()
            counter++
            if (counter > 20) {
                onNoSubscriptionFoundRedirect()
            }
        }
        isSuccessVerified = true
        _isSuccessVerifiedFlow.value = true
    }

    private suspend fun getSubscriptionDetails() {
        _uiState.update { it.copy(isLoading = true) }
        try {
            val details = paymentApi.getSubscriptionDetails()
            if (details != null) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        plan = details.plan,
                        nextBillingDate = details.nextBillingDate,
                        isCancelled = details.isCancelled
                    )
                }
            } else {
                _uiState.update { it.copy(isLoading = false, error = "Failed to load subscription details.") }
            }
        } catch (e: Exception) {
            _uiState.update { it.copy(isLoading = false, error = e.message) }
        }
    }

    fun reset() {
        isPageLoaded = false
        _isPageLoadedFlow.value = false
        isSuccessVerified = false
        _isSuccessVerifiedFlow.value = false
        _uiState.value = SubscriptionDetailsUiState()
    }

    fun cancelSubscription() {
        // Play owns the subscription lifecycle on Android; the backend has no way to cancel it, so
        // the user is sent to the Play Store instead.
        if (usesPlayBilling) {
            openPlayStoreSubscriptions()
            return
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            try {
                paymentApi.cancelSubscription()
                _uiState.update { it.copy(isCancelled = true, isLoading = false) }
            } catch (e: Exception) {
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
    }
}